import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSessionData } from '../../components/sessionContext'
import useUpdateEffect from '../../utils/useUpdateEffect'

// react router
import { Route, Switch, withRouter } from 'react-router-dom'

// scenes
import Home from './scenes/Home'
import Devices from './scenes/Devices'
import CreateDevices from './scenes/CreateDevices'
import Device from './scenes/Device'
import Sensors from './scenes/Sensors'
import UpsertSensor from './scenes/UpsertSensor'
import Users from './scenes/Users'
import UpsertUser from './scenes/UpsertUser'
import PDF from './scenes/PDF'

// components
import UserContextProvider from './components/UserContext'
import Topbar from './components/Topbar'
import Footer from './components/Footer'
import { NoMatch, NetworkError, BadRequest } from './components/Error'

// apollo client
import { ApolloProvider } from '@apollo/react-hooks'
import { ApolloClient } from '@apollo/react-hooks'
import { ApolloLink, split } from 'apollo-link'
import { createUploadLink } from 'apollo-upload-client'
import { WebSocketLink } from 'apollo-link-ws'
import { setContext } from 'apollo-link-context'
import { getMainDefinition } from 'apollo-utilities'
import { onError } from 'apollo-link-error'
import { InMemoryCache } from '@apollo/client'

// styles
import './App.css'

function App(props) {
  // states
  let [appError, setAppError] = useState(false)
  let [loc, setLoc] = useState(useLocation().pathname)

  const session = useSessionData()

  // get the authentication token from local storage if it exists
  const token = session.get('token')

  // initialize apollo client
  let authLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        Authorization: token ? `Bearer ${token}` : '',
      }
    }
  })

  // graphql server uri
  let uri = 'http://localhost:1337/graphql'
  if (session.get('server')) uri = session.get('server') + '/graphql'

  let uploadLink = createUploadLink({
    uri,
    credentials: 'same-origin'
  })

  // websocket link
  let wsLink = new WebSocketLink({
    uri: uri.replace('http:', 'ws:').replace('https:', 'wss:'),
    options: {
      connectionParams: {
        headers: {
          authorization: token ? `Bearer ${token}` : ""
        }
      },
      reconnect: true
    }
  })

  let errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.find(graphQLError => {
        // console.log(graphQLError.extensions)
        if (graphQLError.extensions && graphQLError.extensions.code === 'UNAUTHENTICATED') {
          session.logout()
          props.history.push('/login', { expired: true })
          return true
        } else {
          return false
        }
      })
    }
    if (networkError && networkError.statusCode === 400) {
      setAppError('BadRequest')
    } else if (networkError) {
      setAppError('NetworkError')
    }
  })

  const link = split(
    // split based on operation type
    ({ query }) => {
      const { kind, operation } = getMainDefinition(query)
      return kind === 'OperationDefinition' && operation === 'subscription'
    },
    wsLink,
    ApolloLink.from([authLink, errorLink, uploadLink])
  )
  let client = new ApolloClient({
    link,
    cache: new InMemoryCache()
  })

  const onRouteChanged = () => {
    setAppError(false)
  }

  useUpdateEffect(() => {
    onRouteChanged()
  }, [])

  useUpdateEffect(() => {
    let path = props.location.pathname
    setLoc(path)
  }, [props])

  return (
    <ApolloProvider client={client}>
      <div className={loc === '/pdf' ? 'App Pdf' : 'App'}>
        <UserContextProvider>
          {loc !== '/pdf' && (<Topbar />)}
          {appError ? (
            <>
              {(appError === 'BadRequest') ?
                (<BadRequest />)
                : appError === 'NetworkError' ?
                  (<NetworkError />)
                  : null}
            </>
          ) : (
            <div className='route'>
              <Switch>
                <Route exact path='/' component={Home} />
                <Route path='/pdf' component={PDF} />
                <Route exact path='/devices' component={Devices} />
                <Route exact path='/devices/create' component={CreateDevices} />
                <Route path='/devices/:device' component={Device} />
                <Route exact path='/sensors' component={Sensors} />
                <Route exact path='/sensors/create' component={UpsertSensor} />
                <Route path='/sensors/:sensor' component={UpsertSensor} />
                <Route exact path='/users' component={Users} />
                <Route exact path='/users/create' component={UpsertUser} />
                <Route path='/users/:id' component={UpsertUser}  />
                <Route component={NoMatch} />
              </Switch>
            </div>
          )}
          {loc !== '/pdf' && (<Footer />)}
        </UserContextProvider>
      </div>
    </ApolloProvider>
  )
}

export default withRouter(App)
